import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type MouseEvent } from 'react';
import type {
  Appointment,
  CalAttendee,
  CalOrganizer,
  Content,
  InviteComponent,
} from '@zimbra-webif/sdk';
import {
  useAppointment,
  useCalendarAppts,
  useCalendarFolders,
  useCreateAppointment,
  useModifyAppointment,
  type CalendarApptHit,
  type CalendarFolder,
} from '../lib/calendar-queries';
import { IconCalendar } from './icons';
import { useAccountInfo } from '../lib/settings-queries';
import { useClient } from '../lib/ClientContext';
import { useIsMobile } from '../lib/useIsMobile';
import './calendar.css';

// Danish, Monday-first weekday short labels (man..søn) derived from a known
// Monday (2024-01-01 was a Monday) so we never hardcode/translate by hand.
const WEEKDAYS_MON: string[] = Array.from({ length: 7 }, (_, i) => {
  const d = new Date(2024, 0, 1 + i); // Mon Jan 1 2024 .. Sun Jan 7 2024
  return d.toLocaleDateString('da-DK', { weekday: 'short' });
});
const MAX_CHIPS = 3;
const MIN_EVENT_PCT = 1.6; // minimum timed-event height as % of the 24h column
// Event rendering: Box 1 is the full-width time box (height = duration, colored
// left timeline, holds the time). When it's too short to also hold the text, a
// Box 2 hangs below it (left-aligned, 80% width, square left corners) for the text.
const BOX1_FULL_PX = 46; // ≥ this, Box 1 alone holds time + title + location
const TIME_LINE_PX = 16; // min Box 1 height so the time line stays legible
const BOX2_PX = 40; // Box 2 text-panel height (title + location)
const DAY_MS = 24 * 60 * 60 * 1000;

/** Day-of-week index with Monday = 0 … Sunday = 6 (Danish/ISO convention). */
function mondayIndex(d: Date): number {
  return (d.getDay() + 6) % 7;
}

/** Local midnight for a date. */
function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Same calendar day (local)? */
function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * The 42-cell (6-week) grid covering `month`, starting on the Monday on/before
 * the 1st. Always 6 rows so the grid height is stable across months.
 */
function monthGridDays(month: Date): Date[] {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const start = startOfDay(first);
  start.setDate(start.getDate() - mondayIndex(start)); // back to Monday on/before the 1st
  const days: Date[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push(d);
  }
  return days;
}

/** A flattened instance: one appointment instance pinned to a start time. */
interface EventInstance {
  apptId: string;
  title: string;
  loc: string;
  start: number; // ms epoch
  durMs: number;
  allDay: boolean;
  folderId: string; // owning calendar folder id (CalendarSummary `l`)
}

function isAllDay(hit: CalendarApptHit): boolean {
  return hit.allDay === '1';
}

/** Flatten Search `<appt>` hits into per-instance events. */
function flattenInstances(hits: CalendarApptHit[]): EventInstance[] {
  const out: EventInstance[] = [];
  for (const hit of hits) {
    const id = hit.id;
    if (id === undefined) continue;
    const title = hit.name ?? '(no title)';
    const loc = hit.loc ?? '';
    const allDay = isAllDay(hit);
    const durMs = typeof hit.dur === 'number' ? hit.dur : 0;
    const folderId = typeof hit.l === 'string' ? hit.l : String(hit.l ?? '');
    for (const inst of hit.inst ?? []) {
      if (typeof inst.s !== 'number') continue;
      out.push({ apptId: String(id), title, loc, start: inst.s, durMs, allDay, folderId });
    }
  }
  return out;
}

function fmtTime(ms: number): string {
  return new Date(ms).toLocaleTimeString('da-DK', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function fmtDateLong(d: Date): string {
  return d.toLocaleDateString('da-DK', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/** getAppointment returns s/e/desc/descHtml as ARRAYS in JSON even though the
 *  type says single — normalise so we don't silently read undefined. */
function asArr<T>(v: T | T[] | undefined | null): T[] {
  return v == null ? [] : Array.isArray(v) ? v : [v];
}
function dtMs(v: unknown): number | undefined {
  const first = asArr(v as { u?: number } | { u?: number }[])[0];
  return typeof first?.u === 'number' ? first.u : undefined;
}
/** Read a `<desc>`/`<descHtml>` field (string | Content | array of those). */
function contentText(v: Content | string | Array<Content | string> | undefined): string {
  const first = asArr(v)[0];
  if (first === undefined) return '';
  return typeof first === 'string' ? first : (first._content ?? '');
}
/** ms epoch → `YYYY-MM-DDTHH:MM` (local) for an <input type="datetime-local">. */
function msToLocalInput(ms: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** The primary (series) component of a fetched appointment. */
function primaryComp(appt: Appointment | undefined): InviteComponent | undefined {
  return appt?.inv?.[0]?.comp?.[0];
}

function organizerLabel(or: CalOrganizer | undefined): string {
  if (!or) return '';
  return or.d ?? or.a ?? '';
}

function attendeeLabel(at: CalAttendee): string {
  return at.d ?? at.a ?? '';
}

/** Detail modal for one appointment, loaded on demand via getAppointment. */
function EventDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const apptQ = useAppointment(id);
  const appt = apptQ.data;
  const comp = primaryComp(appt);

  const modify = useModifyAppointment();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: '', loc: '', start: '', end: '', notes: '' });
  // Only the organizer can meaningfully edit; the server enforces this too.
  const canEdit = comp !== undefined && comp.isOrg !== false;
  const beginEdit = () => {
    setForm({
      name: comp?.name ?? '',
      loc: comp?.loc ?? '',
      start: msToLocalInput(dtMs(comp?.s) ?? Date.now()),
      end: msToLocalInput(dtMs(comp?.e) ?? Date.now()),
      notes: contentText(comp?.desc) || contentText(comp?.descHtml),
    });
    setEditing(true);
  };
  const saveEdit = () => {
    const startMs = new Date(form.start).getTime();
    const endMs = new Date(form.end).getTime();
    if (!form.name.trim() || Number.isNaN(startMs) || Number.isNaN(endMs) || endMs <= startMs) return;
    modify.mutate(
      { id, name: form.name.trim(), loc: form.loc, startMs, endMs, notes: form.notes },
      { onSuccess: () => setEditing(false) },
    );
  };

  const title = comp?.name ?? '(no title)';
  const loc = comp?.loc ?? '';
  const organizer = organizerLabel(comp?.or);
  const attendees = comp?.at ?? [];
  const notes = contentText(comp?.desc) || contentText(comp?.descHtml);

  const startMs = dtMs(comp?.s);
  const endMs = dtMs(comp?.e);
  const allDay = comp?.allDay === '1';
  const fmtDay = (ms: number) =>
    new Date(ms).toLocaleDateString('da-DK', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  const fmtTime = (ms: number) =>
    new Date(ms).toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit', hour12: false });
  let when = '';
  if (startMs !== undefined) {
    if (allDay) {
      when = fmtDay(startMs);
    } else {
      when = `${fmtDay(startMs)} · ${fmtTime(startMs)}`;
      if (endMs !== undefined) when += ` – ${fmtTime(endMs)}`;
    }
  }
  const STATUS: Record<string, string> = { CONF: 'Confirmed', TENT: 'Tentative', CANC: 'Cancelled' };
  const status = comp?.status ? (STATUS[comp.status] ?? comp.status) : '';

  return (
    <div className="cal-overlay" onClick={onClose}>
      <div className="cal-detail" onClick={(e) => e.stopPropagation()}>
        <div className="cal-detail-hdr">
          <h2>{editing ? 'Edit event' : title}</h2>
          {!editing && canEdit && !apptQ.isLoading ? (
            <button className="cal-edit-btn" onClick={beginEdit}>
              Edit
            </button>
          ) : null}
          <button className="cal-close" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="cal-detail-body">
          {apptQ.isLoading ? (
            <div className="cal-field-value">Loading…</div>
          ) : editing ? (
            <div className="cal-edit-form">
              <label className="cal-edit-row">
                <span className="cal-field-label">Title</span>
                <input
                  className="cal-edit-input"
                  value={form.name}
                  autoFocus
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                />
              </label>
              <label className="cal-edit-row">
                <span className="cal-field-label">Location</span>
                <input
                  className="cal-edit-input"
                  value={form.loc}
                  onChange={(e) => setForm((f) => ({ ...f, loc: e.target.value }))}
                />
              </label>
              <label className="cal-edit-row">
                <span className="cal-field-label">Start</span>
                <input
                  type="datetime-local"
                  className="cal-edit-input"
                  value={form.start}
                  onChange={(e) => setForm((f) => ({ ...f, start: e.target.value }))}
                />
              </label>
              <label className="cal-edit-row">
                <span className="cal-field-label">End</span>
                <input
                  type="datetime-local"
                  className="cal-edit-input"
                  value={form.end}
                  onChange={(e) => setForm((f) => ({ ...f, end: e.target.value }))}
                />
              </label>
              <label className="cal-edit-row">
                <span className="cal-field-label">Notes</span>
                <textarea
                  className="cal-edit-input cal-edit-notes"
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                />
              </label>
              {modify.isError ? (
                <div className="cal-edit-error">Couldn’t save — {(modify.error as Error)?.message ?? 'try again'}.</div>
              ) : null}
              <div className="cal-edit-actions">
                <button className="cal-btn" onClick={() => setEditing(false)} disabled={modify.isPending}>
                  Cancel
                </button>
                <button className="cal-btn primary" onClick={saveEdit} disabled={modify.isPending}>
                  {modify.isPending ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          ) : (
            <>
              {when ? (
                <div className="cal-field">
                  <span className="cal-field-label">When</span>
                  <span className="cal-field-value">{when}</span>
                </div>
              ) : null}
              {loc ? (
                <div className="cal-field">
                  <span className="cal-field-label">Location</span>
                  <span className="cal-field-value">{loc}</span>
                </div>
              ) : null}
              {status ? (
                <div className="cal-field">
                  <span className="cal-field-label">Status</span>
                  <span className="cal-field-value">{status}</span>
                </div>
              ) : null}
              {organizer ? (
                <div className="cal-field">
                  <span className="cal-field-label">Organizer</span>
                  <span className="cal-field-value">{organizer}</span>
                </div>
              ) : null}
              {attendees.length ? (
                <div className="cal-field">
                  <span className="cal-field-label">Attendees</span>
                  <span className="cal-field-value">
                    {attendees.map(attendeeLabel).filter(Boolean).join(', ')}
                  </span>
                </div>
              ) : null}
              {notes ? (
                <div className="cal-field">
                  <span className="cal-field-label">Notes</span>
                  <div className="cal-notes">{notes}</div>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- new event ---------- */

/** Where a "new event" was requested from: a day (all-day) or a time slot. */
export interface NewEventSeed {
  startMs: number;
  endMs: number;
  allDay: boolean;
}

const NEW_CAL_KEY = 'zwf.cal.newDefault';

/** ms epoch → `YYYY-MM-DD` (local) for an <input type="date">. */
function msToDateInput(ms: number): string {
  return msToLocalInput(ms).slice(0, 10);
}

/**
 * Create-event sheet. Same overlay/form as the edit sheet plus a calendar
 * picker (own + shared) and an all-day toggle. The chosen calendar is
 * remembered per browser so the next event defaults to it.
 */
function NewEvent({
  seed,
  folders,
  onClose,
}: {
  seed: NewEventSeed;
  folders: CalendarFolder[];
  onClose: () => void;
}) {
  const create = useCreateAppointment();
  const infoQ = useAccountInfo();
  const { session } = useClient();
  const me = infoQ.data?.name ?? session.user;

  const defaultFolder = () => {
    let stored: string | null = null;
    try {
      stored = localStorage.getItem(NEW_CAL_KEY);
    } catch {
      stored = null;
    }
    if (stored && folders.some((f) => f.id === stored)) return stored;
    return folders.find((f) => !f.shared)?.id ?? folders[0]?.id ?? '10';
  };
  const [form, setForm] = useState(() => ({
    name: '',
    loc: '',
    folderId: defaultFolder(),
    allDay: seed.allDay,
    start: msToLocalInput(seed.startMs),
    end: msToLocalInput(seed.endMs),
    date: msToDateInput(seed.startMs),
    // last day of an all-day event (inclusive, what the user thinks in)
    dateEnd: msToDateInput(seed.allDay ? seed.endMs - 1 : seed.endMs),
    attendees: '',
    notes: '',
  }));

  const attendees = form.attendees
    .split(/[,;\s]+/)
    .map((a) => a.trim())
    .filter(Boolean);
  const badAttendee = attendees.find((a) => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(a));

  let startMs: number;
  let endMs: number;
  if (form.allDay) {
    startMs = new Date(`${form.date}T00:00`).getTime();
    const last = new Date(`${form.dateEnd}T00:00`);
    last.setDate(last.getDate() + 1);
    endMs = last.getTime();
  } else {
    startMs = new Date(form.start).getTime();
    endMs = new Date(form.end).getTime();
  }
  const valid =
    form.name.trim() !== '' &&
    !Number.isNaN(startMs) &&
    !Number.isNaN(endMs) &&
    endMs > startMs &&
    badAttendee === undefined;

  const save = () => {
    if (!valid) return;
    const folder = folders.find((f) => f.id === form.folderId);
    create.mutate(
      {
        folderId: form.folderId,
        name: form.name.trim(),
        loc: form.loc.trim(),
        startMs,
        endMs,
        allDay: form.allDay,
        notes: form.notes,
        attendees,
        me,
        owner: folder?.shared ? folder.owner : undefined,
      },
      {
        onSuccess: () => {
          try {
            localStorage.setItem(NEW_CAL_KEY, form.folderId);
          } catch {
            /* storage may be unavailable */
          }
          onClose();
        },
      },
    );
  };

  // Keep end after start when the user moves the start (like Apple Calendar).
  const setStart = (v: string) => {
    setForm((f) => {
      const oldS = new Date(f.start).getTime();
      const oldE = new Date(f.end).getTime();
      const newS = new Date(v).getTime();
      const dur = !Number.isNaN(oldS) && !Number.isNaN(oldE) && oldE > oldS ? oldE - oldS : 3600_000;
      return { ...f, start: v, end: Number.isNaN(newS) ? f.end : msToLocalInput(newS + dur) };
    });
  };

  return (
    <div className="cal-overlay" onClick={onClose}>
      <div className="cal-detail" onClick={(e) => e.stopPropagation()}>
        <div className="cal-detail-hdr">
          <h2>New event</h2>
          <button className="cal-close" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="cal-detail-body">
          <div className="cal-edit-form">
            <label className="cal-edit-row">
              <span className="cal-field-label">Title</span>
              <input
                className="cal-edit-input"
                value={form.name}
                autoFocus
                placeholder="New event"
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') save();
                }}
              />
            </label>
            <label className="cal-edit-row">
              <span className="cal-field-label">Calendar</span>
              <select
                className="cal-edit-input"
                value={form.folderId}
                onChange={(e) => setForm((f) => ({ ...f, folderId: e.target.value }))}
              >
                {folders.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                    {f.shared ? ` — ${f.owner ?? 'shared'}` : ''}
                  </option>
                ))}
              </select>
            </label>
            <label className="cal-edit-row cal-edit-inline">
              <input
                type="checkbox"
                checked={form.allDay}
                onChange={(e) => setForm((f) => ({ ...f, allDay: e.target.checked }))}
              />
              <span className="cal-field-label">All day</span>
            </label>
            {form.allDay ? (
              <>
                <label className="cal-edit-row">
                  <span className="cal-field-label">From</span>
                  <input
                    type="date"
                    className="cal-edit-input"
                    value={form.date}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        date: e.target.value,
                        dateEnd: f.dateEnd < e.target.value ? e.target.value : f.dateEnd,
                      }))
                    }
                  />
                </label>
                <label className="cal-edit-row">
                  <span className="cal-field-label">To</span>
                  <input
                    type="date"
                    className="cal-edit-input"
                    value={form.dateEnd}
                    min={form.date}
                    onChange={(e) => setForm((f) => ({ ...f, dateEnd: e.target.value }))}
                  />
                </label>
              </>
            ) : (
              <>
                <label className="cal-edit-row">
                  <span className="cal-field-label">Start</span>
                  <input
                    type="datetime-local"
                    className="cal-edit-input"
                    value={form.start}
                    onChange={(e) => setStart(e.target.value)}
                  />
                </label>
                <label className="cal-edit-row">
                  <span className="cal-field-label">End</span>
                  <input
                    type="datetime-local"
                    className="cal-edit-input"
                    value={form.end}
                    min={form.start}
                    onChange={(e) => setForm((f) => ({ ...f, end: e.target.value }))}
                  />
                </label>
              </>
            )}
            <label className="cal-edit-row">
              <span className="cal-field-label">Location</span>
              <input
                className="cal-edit-input"
                value={form.loc}
                onChange={(e) => setForm((f) => ({ ...f, loc: e.target.value }))}
              />
            </label>
            <label className="cal-edit-row">
              <span className="cal-field-label">Invitees</span>
              <input
                className="cal-edit-input"
                value={form.attendees}
                placeholder="email, email, …"
                onChange={(e) => setForm((f) => ({ ...f, attendees: e.target.value }))}
              />
            </label>
            <label className="cal-edit-row">
              <span className="cal-field-label">Notes</span>
              <textarea
                className="cal-edit-input cal-edit-notes"
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </label>
            {badAttendee !== undefined ? (
              <div className="cal-edit-error">“{badAttendee}” is not an email address.</div>
            ) : null}
            {!Number.isNaN(startMs) && !Number.isNaN(endMs) && endMs <= startMs ? (
              <div className="cal-edit-error">End must be after start.</div>
            ) : null}
            {create.isError ? (
              <div className="cal-edit-error">Couldn’t create — {(create.error as Error)?.message ?? 'try again'}.</div>
            ) : null}
            <div className="cal-edit-actions">
              <button className="cal-btn" onClick={onClose} disabled={create.isPending}>
                Cancel
              </button>
              <button className="cal-btn primary" onClick={save} disabled={!valid || create.isPending}>
                {create.isPending ? 'Creating…' : attendees.length ? 'Send' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- calendar colors + view state (localStorage-backed) ---------- */

type CalView = 'day' | 'workweek' | 'week' | 'month';

// We persist the DISABLED set (calendars the user explicitly hid), so every
// calendar — including newly-discovered shared ones — shows by default.
const DISABLED_KEY = 'zwf.cal.disabled';
const COLOR_KEY = (id: string) => `zwf.cal.color.${id}`;

/** A pleasant, glass-friendly palette; cycled by calendar index. */
const PALETTE = [
  '#0a66ff', '#ff3b30', '#34c759', '#ff9500', '#af52de',
  '#ff2d92', '#5ac8fa', '#ffcc00', '#5856d6', '#30b0c7',
];

function paletteColor(index: number): string {
  return PALETTE[index % PALETTE.length] ?? '#0a66ff';
}

/** Zimbra numeric folder color index (0-9) → an approximate hex default. */
const ZIMBRA_COLOR_HEX: Record<number, string> = {
  0: '#808a96', 1: '#2f6df6', 2: '#0fb6c8', 3: '#28a745', 4: '#9b59b6',
  5: '#e0314f', 6: '#f6c500', 7: '#ff5fa2', 8: '#7f8c8d', 9: '#ff8c1a',
};
function colorIndexHex(idx: number | undefined): string | undefined {
  return idx !== undefined ? ZIMBRA_COLOR_HEX[idx] : undefined;
}

/** Normalize a stored / Zimbra rgb value to `#rrggbb`, or undefined if invalid. */
function normHex(v: string | undefined): string | undefined {
  if (!v) return undefined;
  const m = /^#?([0-9a-fA-F]{6})$/.exec(v.trim());
  return m ? `#${(m[1] ?? '').toLowerCase()}` : undefined;
}

/** Read the persisted DISABLED-id set (calendars the user hid). */
function readDisabled(): Set<string> {
  try {
    const raw = localStorage.getItem(DISABLED_KEY);
    if (raw !== null) {
      const arr = JSON.parse(raw) as unknown;
      if (Array.isArray(arr)) return new Set(arr.map(String));
    }
  } catch {
    /* ignore malformed pref */
  }
  return new Set();
}

function writeDisabled(ids: Set<string>): void {
  try {
    localStorage.setItem(DISABLED_KEY, JSON.stringify([...ids]));
  } catch {
    /* storage may be unavailable */
  }
}

/** Hex → rgba string at the given alpha (for soft event backgrounds). */
function hexAlpha(hex: string, alpha: number): string {
  const h = normHex(hex) ?? '#0a66ff';
  const r = parseInt(h.slice(1, 3), 16);
  const g = parseInt(h.slice(3, 5), 16);
  const b = parseInt(h.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Minutes since local midnight for a ms-epoch timestamp. */
function minutesFromMidnight(ms: number): number {
  const d = new Date(ms);
  return d.getHours() * 60 + d.getMinutes();
}

/* ---------- timed event layout ---------- */

interface PositionedEvent {
  ev: EventInstance;
  /** % from top of the 24h column (0..100) */
  top: number;
  /** % of the 24h column height */
  height: number;
  /** % from left of the column */
  left: number;
  /** % width of the column */
  width: number;
}

/**
 * Percentage geometry for timed events; overlapping events are split into
 * side-by-side columns (greedy interval colouring per overlap cluster).
 */
function layoutTimed(events: EventInstance[], minVisualMin = 20): PositionedEvent[] {
  // Overlap is computed on the VISUAL extent, not just the time span: a short
  // event whose body is taller than its slot (minVisualMin) must still go
  // side-by-side with whatever it visually covers below it.
  const items = events
    .filter((e) => !e.allDay)
    .map((ev) => {
      const startMin = minutesFromMidnight(ev.start);
      return { ev, startMin, endMin: startMin + Math.max(ev.durMs / 60000, minVisualMin), col: 0, ncols: 1 };
    })
    .sort((a, b) => a.startMin - b.startMin || b.endMin - a.endMin);

  let i = 0;
  while (i < items.length) {
    const cluster = [items[i]!];
    let clusterEnd = items[i]!.endMin;
    let j = i + 1;
    while (j < items.length && items[j]!.startMin < clusterEnd) {
      cluster.push(items[j]!);
      clusterEnd = Math.max(clusterEnd, items[j]!.endMin);
      j++;
    }
    const colEnds: number[] = [];
    for (const it of cluster) {
      let placed = -1;
      for (let c = 0; c < colEnds.length; c++) {
        if (it.startMin >= (colEnds[c] ?? 0)) {
          colEnds[c] = it.endMin;
          placed = c;
          break;
        }
      }
      if (placed === -1) {
        colEnds.push(it.endMin);
        placed = colEnds.length - 1;
      }
      it.col = placed;
    }
    for (const it of cluster) it.ncols = colEnds.length;
    i = j;
  }

  return items.map((it) => ({
    ev: it.ev,
    top: (it.startMin / 1440) * 100,
    height: Math.max((it.ev.durMs / DAY_MS) * 100, MIN_EVENT_PCT),
    left: (it.col / it.ncols) * 100,
    width: (1 / it.ncols) * 100,
  }));
}

/* ---------- shared chip/event renderers ---------- */

function eventStyle(color: string): CSSProperties {
  return {
    background: hexAlpha(color, 0.16),
    borderLeft: `3px solid ${color}`,
    color: 'var(--text)',
  };
}

/** A single day's column inside a time grid (day/week views). */
function TimeColumn({
  day,
  events,
  colorOf,
  onOpen,
  today,
  onPickDay,
  onNew,
}: {
  day: Date;
  events: EventInstance[];
  colorOf: (folderId: string) => string;
  onOpen: (apptId: string) => void;
  today: Date;
  onPickDay: (d: Date) => void;
  onNew: (seed: NewEventSeed) => void;
}) {
  const allDay = events.filter((e) => e.allDay);
  const isToday = sameDay(day, today);
  // measure the body's pixel height so we can detect events too short to fit text
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const [bodyH, setBodyH] = useState(0);
  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    const update = () => setBodyH(el.clientHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // minutes a short event occupies visually (Box 1 + Box 2) — drives overlap columns
  const minVisualMin = bodyH > 0 ? ((TIME_LINE_PX + BOX2_PX) / bodyH) * 1440 : 60;
  const positioned = layoutTimed(events, minVisualMin);

  // Click on empty space → new 1h event at that time, snapped to the half hour.
  const newAtSlot = (e: MouseEvent<HTMLDivElement>) => {
    const el = bodyRef.current;
    if (!el) return;
    const y = e.clientY - el.getBoundingClientRect().top;
    const minutes = Math.floor(((y / el.clientHeight) * 1440) / 30) * 30;
    const start = startOfDay(day);
    start.setMinutes(Math.max(0, Math.min(23 * 60, minutes)));
    onNew({ startMs: start.getTime(), endMs: start.getTime() + 3600_000, allDay: false });
  };
  const newAllDay = () => {
    const start = startOfDay(day);
    const end = new Date(start);
    end.setDate(end.getDate() + 1);
    onNew({ startMs: start.getTime(), endMs: end.getTime(), allDay: true });
  };

  return (
    <div className="cal-tcol">
      <button
        className={`cal-tcol-hdr${isToday ? ' today' : ''}`}
        onClick={() => onPickDay(startOfDay(day))}
      >
        <span className="cal-tcol-wd">{WEEKDAYS_MON[mondayIndex(day)] ?? ''}</span>
        <span className="cal-tcol-dn">{day.getDate()}</span>
      </button>
      {allDay.length ? (
        <div className="cal-tcol-allday" onClick={newAllDay} title="New all-day event">
          {allDay.map((ev, i) => {
            const color = colorOf(ev.folderId);
            return (
              <div
                key={`${ev.apptId}-${ev.start}-${i}`}
                className="cal-allday-chip"
                style={eventStyle(color)}
                title={ev.title}
                onClick={(e) => {
                  e.stopPropagation();
                  onOpen(ev.apptId);
                }}
              >
                {ev.title}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="cal-tcol-allday empty" onClick={newAllDay} title="New all-day event" />
      )}
      <div className="cal-tcol-body" ref={bodyRef} onClick={newAtSlot}>
        {Array.from({ length: 24 }, (_, h) => (
          <div key={h} className="cal-hour-line" style={{ top: `${(h / 24) * 100}%` }} />
        ))}
        {positioned.map(({ ev, top, height, left, width }, i) => {
          const color = colorOf(ev.folderId);
          // Box 1 = full-width time box, height = real duration (colored left
          // timeline + the time). If too short to also hold the text, Box 2 hangs
          // below (left-aligned, 80% width, square left corners, merges with Box 1).
          const durPx = bodyH > 0 ? (height / 100) * bodyH : 0;
          const needsBox2 = bodyH > 0 && durPx < BOX1_FULL_PX;
          const box1Px = needsBox2 ? Math.max(durPx, TIME_LINE_PX) : Math.max(durPx, BOX1_FULL_PX);
          const endMs = ev.start + ev.durMs;
          const fill = hexAlpha(color, 0.16);
          const leftCss = `calc(${left}% + 2px)`;
          const widthCss = `calc(${width}% - 4px)`;
          return (
            <Fragment key={`${ev.apptId}-${ev.start}-${i}`}>
              <div
                className={`cal-tevent${needsBox2 ? ' has2' : ''}`}
                style={{ top: `${top}%`, height: `${box1Px}px`, left: leftCss, width: widthCss, background: fill, color: 'var(--text)' }}
                title={ev.title}
                onClick={(e) => {
                  e.stopPropagation();
                  onOpen(ev.apptId);
                }}
              >
                <span className="cal-tbar" style={{ background: color }} />
                <span className="cal-tevent-time" style={{ color }}>
                  {fmtTime(ev.start)}
                  {ev.durMs > 0 ? ` – ${fmtTime(endMs)}` : ''}
                </span>
                {needsBox2 ? null : (
                  <>
                    <span className="cal-tevent-title">{ev.title}</span>
                    {ev.loc ? <span className="cal-tevent-loc">{ev.loc}</span> : null}
                  </>
                )}
              </div>
              {needsBox2 ? (
                <div
                  className="cal-tbox2"
                  style={{
                    top: `calc(${top}% + ${box1Px}px)`,
                    height: `${BOX2_PX}px`,
                    left: leftCss,
                    width: `calc((${widthCss}) * 0.8)`,
                    background: fill,
                    color: 'var(--text)',
                  }}
                  title={ev.title}
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpen(ev.apptId);
                  }}
                >
                  <span className="cal-tevent-title">{ev.title}</span>
                  {ev.loc ? <span className="cal-tevent-loc">{ev.loc}</span> : null}
                </div>
              ) : null}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}

/** Day or week time grid sharing one hour gutter. */
function TimeGrid({
  days,
  byDay,
  colorOf,
  onOpen,
  today,
  onPickDay,
  onNew,
}: {
  days: Date[];
  byDay: Map<number, EventInstance[]>;
  colorOf: (folderId: string) => string;
  onOpen: (apptId: string) => void;
  today: Date;
  onPickDay: (d: Date) => void;
  onNew: (seed: NewEventSeed) => void;
}) {
  return (
    <div className="cal-timegrid">
      <div className="cal-gutter">
        <div className="cal-gutter-hdr" />
        <div className="cal-gutter-allday" />
        <div className="cal-gutter-body">
          {Array.from({ length: 24 }, (_, h) => (
            <div key={h} className="cal-gutter-hour" style={{ top: `${(h / 24) * 100}%` }}>
              {h === 0 ? '' : String(h).padStart(2, '0')}
            </div>
          ))}
        </div>
      </div>
      <div className="cal-tcols">
        {days.map((day) => (
          <TimeColumn
            key={day.getTime()}
            day={day}
            events={byDay.get(startOfDay(day).getTime()) ?? []}
            colorOf={colorOf}
            onOpen={onOpen}
            today={today}
            onPickDay={onPickDay}
            onNew={onNew}
          />
        ))}
      </div>
    </div>
  );
}

export function CalendarScreen() {
  const [view, setView] = useState<CalView>('month');
  // Anchor date the views pivot around.
  const [anchor, setAnchor] = useState(() => startOfDay(new Date()));
  const [selectedDay, setSelectedDay] = useState(() => startOfDay(new Date()));
  const [openApptId, setOpenApptId] = useState<string | null>(null);
  const [newSeed, setNewSeed] = useState<NewEventSeed | null>(null);

  const today = useMemo(() => startOfDay(new Date()), []);
  const foldersQ = useCalendarFolders();
  const folders: CalendarFolder[] = useMemo(() => foldersQ.data ?? [], [foldersQ.data]);

  // ----- disabled set (localStorage); everything else shows by default -----
  const [disabled, setDisabled] = useState<Set<string>>(() => readDisabled());

  const toggleEnabled = useCallback((id: string) => {
    setDisabled((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); // re-enable
      else next.add(id); // hide
      writeDisabled(next);
      return next;
    });
  }, []);

  // ----- per-calendar colors (localStorage) -----
  const [colors, setColors] = useState<Record<string, string>>({});
  // Seed colors when the folder list arrives: stored pref > Zimbra rgb > palette.
  useEffect(() => {
    if (!folders.length) return;
    setColors((prev) => {
      const next = { ...prev };
      folders.forEach((f, i) => {
        if (next[f.id]) return;
        let stored: string | null = null;
        try {
          stored = localStorage.getItem(COLOR_KEY(f.id));
        } catch {
          stored = null;
        }
        next[f.id] =
          normHex(stored ?? undefined) ?? normHex(f.rgb) ?? colorIndexHex(f.color) ?? paletteColor(i);
      });
      return next;
    });
  }, [folders]);

  const setCalColor = useCallback((id: string, color: string) => {
    const hex = normHex(color) ?? color;
    setColors((prev) => ({ ...prev, [id]: hex }));
    try {
      localStorage.setItem(COLOR_KEY(id), hex);
    } catch {
      /* storage may be unavailable */
    }
  }, []);

  // Map an appointment hit's `l` value to the local calendar folder id.
  // Own calendars: `l` === folder.id. Shared mountpoints: `l` === "<zid>:<rid>".
  const eventLToFolderId = useMemo(() => {
    const map = new Map<string, string>();
    for (const f of folders) {
      map.set(f.id, f.id);
      if (f.shared && f.zid !== undefined && f.rid !== undefined) {
        map.set(`${f.zid}:${f.rid}`, f.id);
      }
    }
    return map;
  }, [folders]);

  const colorOf = useCallback(
    (eventL: string): string => {
      const folderId = eventLToFolderId.get(eventL) ?? eventL;
      return colors[folderId] ?? PALETTE[0] ?? '#0a66ff';
    },
    [colors, eventLToFolderId],
  );

  // Enabled ids in stable folder order; drives the search query.
  const enabledIds = useMemo(
    () => folders.filter((f) => !disabled.has(f.id)).map((f) => f.id),
    [folders, disabled],
  );

  // ----- visible range per view -----
  const monthAnchor = useMemo(
    () => new Date(anchor.getFullYear(), anchor.getMonth(), 1),
    [anchor],
  );
  const gridDays = useMemo(() => monthGridDays(monthAnchor), [monthAnchor]);

  // Monday of the anchor's week (Danish/ISO week start).
  const weekStart = useMemo(() => {
    const d = startOfDay(anchor);
    d.setDate(d.getDate() - mondayIndex(d));
    return d;
  }, [anchor]);
  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      return d;
    });
  }, [weekStart]);

  const { rangeStart, rangeEnd } = useMemo(() => {
    if (view === 'month') {
      const s = (gridDays[0] ?? monthAnchor).getTime();
      const e = (gridDays[gridDays.length - 1] ?? monthAnchor).getTime() + DAY_MS;
      return { rangeStart: s, rangeEnd: e };
    }
    if (view === 'week') {
      return { rangeStart: weekStart.getTime(), rangeEnd: weekStart.getTime() + 7 * DAY_MS };
    }
    if (view === 'workweek') {
      return { rangeStart: weekStart.getTime(), rangeEnd: weekStart.getTime() + 5 * DAY_MS };
    }
    const s = startOfDay(anchor).getTime();
    return { rangeStart: s, rangeEnd: s + DAY_MS };
  }, [view, gridDays, monthAnchor, weekStart, anchor]);

  const apptsQ = useCalendarAppts(rangeStart, rangeEnd, enabledIds);

  const events = useMemo(() => flattenInstances(apptsQ.data ?? []), [apptsQ.data]);

  // Group instances by local day-start (ms) for fast per-cell lookup.
  const byDay = useMemo(() => {
    const map = new Map<number, EventInstance[]>();
    for (const ev of events) {
      const key = startOfDay(new Date(ev.start)).getTime();
      const list = map.get(key);
      if (list) list.push(ev);
      else map.set(key, [ev]);
    }
    for (const list of map.values()) {
      list.sort((a, b) => Number(b.allDay) - Number(a.allDay) || a.start - b.start);
    }
    return map;
  }, [events]);

  const weeks = useMemo(() => {
    const rows: Date[][] = [];
    for (let i = 0; i < gridDays.length; i += 7) rows.push(gridDays.slice(i, i + 7));
    return rows;
  }, [gridDays]);

  // ----- toolbar nav -----
  function shift(delta: number) {
    setAnchor((a) => {
      if (view === 'month') return new Date(a.getFullYear(), a.getMonth() + delta, 1);
      const d = new Date(a);
      d.setDate(d.getDate() + delta * (view === 'week' || view === 'workweek' ? 7 : 1));
      return startOfDay(d);
    });
  }

  function goToday() {
    const now = startOfDay(new Date());
    setAnchor(now);
    setSelectedDay(now);
  }

  /** "New event" from the toolbar/agenda: on the selected day, 09:00 (or the next full hour if today). */
  function newOnDay(d: Date) {
    const start = startOfDay(d);
    const now = new Date();
    start.setHours(sameDay(start, now) ? Math.min(23, now.getHours() + 1) : 9);
    setNewSeed({ startMs: start.getTime(), endMs: start.getTime() + 3600_000, allDay: false });
  }

  function openDay(d: Date) {
    setAnchor(startOfDay(d));
    setSelectedDay(startOfDay(d));
    setView('day');
  }

  // ----- title per view -----
  const title = useMemo(() => {
    if (view === 'month') {
      return monthAnchor.toLocaleDateString('da-DK', { month: 'long', year: 'numeric' });
    }
    if (view === 'day') return fmtDateLong(anchor);
    const end = (view === 'workweek' ? weekDays[4] : weekDays[6]) ?? weekStart;
    const sameMonth = weekStart.getMonth() === end.getMonth();
    const startStr = weekStart.toLocaleDateString('da-DK', { month: 'short', day: 'numeric' });
    const endStr = end.toLocaleDateString('da-DK', {
      month: sameMonth ? undefined : 'short',
      day: 'numeric',
      year: 'numeric',
    });
    return `${startStr} – ${endStr}`;
  }, [view, monthAnchor, anchor, weekStart, weekDays]);

  const selectedEvents = byDay.get(selectedDay.getTime()) ?? [];
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <div className={`cal${isMobile ? ' m-shell' : ''}${drawerOpen ? ' m-drawer' : ''}`}>
      {isMobile && drawerOpen ? (
        <div className="m-backdrop" onClick={() => setDrawerOpen(false)} />
      ) : null}
      <section className="pane cal-main">
        <div className="pane-hdr cal-toolbar">
          <button className="m-burger" aria-label="Calendars" onClick={() => setDrawerOpen(true)}>
            ☰
          </button>
          <span className="cal-title">{title}</span>
          <div className="seg cal-seg">
            <button className={view === 'day' ? 'on' : ''} onClick={() => setView('day')}>
              Day
            </button>
            <button className={view === 'workweek' ? 'on' : ''} onClick={() => setView('workweek')}>
              Work Week
            </button>
            <button className={view === 'week' ? 'on' : ''} onClick={() => setView('week')}>
              Week
            </button>
            <button className={view === 'month' ? 'on' : ''} onClick={() => setView('month')}>
              Month
            </button>
          </div>
          <div className="cal-nav">
            <button className="cal-btn primary" onClick={() => newOnDay(selectedDay)} title="New event">
              + New
            </button>
            <button className="cal-btn" onClick={goToday}>
              Today
            </button>
            <button className="cal-btn icon" aria-label="Previous" onClick={() => shift(-1)}>
              ‹
            </button>
            <button className="cal-btn icon" aria-label="Next" onClick={() => shift(1)}>
              ›
            </button>
          </div>
        </div>

        {view === 'month' ? (
          <div className="cal-grid">
            <div className="cal-weekdays">
              {WEEKDAYS_MON.map((w, i) => (
                <div key={i} className="cal-weekday">
                  {w}
                </div>
              ))}
            </div>
            <div className="cal-weeks">
              {weeks.map((week, wi) => (
                <div key={wi} className="cal-week">
                  {week.map((day) => {
                    const dayEvents = byDay.get(day.getTime()) ?? [];
                    const outside = day.getMonth() !== monthAnchor.getMonth();
                    const cls = [
                      'cal-day',
                      outside ? 'outside' : '',
                      sameDay(day, today) ? 'today' : '',
                      sameDay(day, selectedDay) ? 'selected' : '',
                    ]
                      .filter(Boolean)
                      .join(' ');
                    return (
                      <div
                        key={day.getTime()}
                        className={cls}
                        onClick={() => setSelectedDay(startOfDay(day))}
                        onDoubleClick={() => openDay(day)}
                      >
                        <span className="cal-day-num">{day.getDate()}</span>
                        <div className="cal-day-events">
                          {dayEvents.slice(0, MAX_CHIPS).map((ev, i) => {
                            const color = colorOf(ev.folderId);
                            return (
                              <div
                                key={`${ev.apptId}-${ev.start}-${i}`}
                                className={`cal-chip${ev.allDay ? ' allday' : ''}`}
                                style={eventStyle(color)}
                                title={ev.title}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedDay(startOfDay(day));
                                  setOpenApptId(ev.apptId);
                                }}
                              >
                                {ev.allDay ? '' : `${fmtTime(ev.start)} `}
                                {ev.title}
                              </div>
                            );
                          })}
                          {dayEvents.length > MAX_CHIPS ? (
                            <div className="cal-chip-more">
                              +{dayEvents.length - MAX_CHIPS} more
                            </div>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="cal-grid cal-grid-time">
            <TimeGrid
              days={view === 'week' ? weekDays : view === 'workweek' ? weekDays.slice(0, 5) : [startOfDay(anchor)]}
              byDay={byDay}
              colorOf={colorOf}
              onOpen={setOpenApptId}
              today={today}
              onPickDay={openDay}
              onNew={setNewSeed}
            />
          </div>
        )}
      </section>

      <aside className="pane cal-calendars m-side">
        <div className="pane-hdr">Calendars</div>
        <div className="pane-scroll cal-cal-list">
          {foldersQ.isLoading ? (
            <div className="loading">Loading…</div>
          ) : folders.length === 0 ? (
            <div className="empty">
              <IconCalendar className="placeholder-icon" />
              <span>No calendars</span>
            </div>
          ) : (
            folders.map((f) => (
              <label key={f.id} className="cal-cal-row">
                <input
                  type="checkbox"
                  className="cal-cal-check"
                  checked={!disabled.has(f.id)}
                  onChange={() => toggleEnabled(f.id)}
                />
                <input
                  type="color"
                  className="cal-cal-swatch"
                  value={colorOf(f.id)}
                  onChange={(e) => setCalColor(f.id, e.target.value)}
                  aria-label={`Color for ${f.name}`}
                />
                <span className="cal-cal-meta">
                  <span className="cal-cal-name">{f.name}</span>
                  {f.shared ? (
                    <span className="cal-cal-owner">{f.owner ?? 'shared'}</span>
                  ) : null}
                </span>
              </label>
            ))
          )}
        </div>

        {view === 'month' ? (
          <div className="cal-agenda-inline">
            <div className="cal-agenda-date">
              {fmtDateLong(selectedDay)}
              <button className="cal-btn icon cal-agenda-add" aria-label="New event" title="New event" onClick={() => newOnDay(selectedDay)}>
                +
              </button>
            </div>
            <div className="cal-agenda-list">
              {apptsQ.isLoading ? (
                <div className="loading">Loading…</div>
              ) : selectedEvents.length === 0 ? (
                <div className="empty small">
                  <span>No events</span>
                </div>
              ) : (
                selectedEvents.map((ev, i) => {
                  const color = colorOf(ev.folderId);
                  return (
                    <div
                      key={`${ev.apptId}-${ev.start}-${i}`}
                      className={`cal-event${openApptId === ev.apptId ? ' active' : ''}`}
                      onClick={() => setOpenApptId(ev.apptId)}
                    >
                      <div className="cal-event-rail" style={{ background: color }} />
                      <div className="cal-event-body">
                        <div className="cal-event-time" style={{ color }}>
                          {ev.allDay ? 'All day' : fmtTime(ev.start)}
                        </div>
                        <div className="cal-event-title">{ev.title}</div>
                        {ev.loc ? <div className="cal-event-loc">{ev.loc}</div> : null}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        ) : null}
      </aside>

      {openApptId !== null ? (
        <EventDetail id={openApptId} onClose={() => setOpenApptId(null)} />
      ) : null}
      {newSeed !== null ? (
        <NewEvent seed={newSeed} folders={folders} onClose={() => setNewSeed(null)} />
      ) : null}
    </div>
  );
}
